"use client"

import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { StoreCart, StoreProduct, StoreProductReview, StoreProductVariant } from "@/types/store"
import { Container } from "@medusajs/ui"
import PlaceholderImage from "@modules/common/icons/placeholder-image"
import { Rating } from "@mui/material"
import { Heart, MessageCirclePlus, MessagesSquare } from "lucide-react"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useState, useTransition } from "react"
import ProductSelect from "../product-select"
import { normalizeTextEncoding } from "@/lib/util/text-encoding"
import { useTranslation } from "react-i18next"
import { addFavorite, removeFavorite } from "@/lib/data/favorites"
import { getCustomer } from "@/lib/data/customer"
import { useToast } from "@/hooks/use-toast"
import { getProductVariantParam } from "@/lib/util/product-variant-url"
import { resolveImageUrl } from "@/lib/util/image-url"

const ProductField = ({
  product,
  reviews,
  initialVariantId,
  cart,
}: {
  product: StoreProduct
  reviews?: StoreProductReview | null
  initialVariantId?: string
  cart: StoreCart | null
}) => {
  const { t } = useTranslation()
  const reviewList = reviews?.data ?? []
  const reviewCount = reviewList.length
  const averageRating =
    reviewCount > 0
      ? Math.round((reviewList.reduce((acc, r) => acc + (r.rating ?? 0), 0) / reviewCount) * 2) / 2
      : 0
  const searchParams = useSearchParams()
  const [relatedProducts, setRelatedProducts] = useState<StoreProduct[]>([])
  const [isClient, setIsClient] = useState(false)
  const [isFavorited, setIsFavorited] = useState(false)

  const findVariantByParam = useCallback((variantParam?: string | null) => {
    if (!variantParam) return undefined

    return product.variants?.find((variant) => {
      const title = variant.title ?? ""

      return title === variantParam || getProductVariantParam(title) === variantParam
    })
  }, [product.variants])

  const getInitialVariant = () => {
    if (initialVariantId) {
      const variant = findVariantByParam(initialVariantId)
      if (variant) {
        return variant
      }
    }

    const urlVariantId = searchParams?.get("variant")
    if (urlVariantId) {
      const variant = findVariantByParam(urlVariantId)
      if (variant) {
        return variant
      }
    }

    const firstVariant = product.variants?.[0]
    return firstVariant
  }

  const [selectedVariant, setSelectedVariant] = useState<
    StoreProductVariant | undefined
  >(getInitialVariant)

  useEffect(() => {
    const variantId = searchParams?.get("variant")
    if (variantId) {
      const variant = findVariantByParam(variantId)
      if (variant) {
        setSelectedVariant(variant)
      }
    }
  }, [searchParams, product.variants, findVariantByParam])

  useEffect(() => {
    setIsClient(true)
    if (selectedVariant) {
      setIsFavorited(!!selectedVariant.is_favorite)
    }
  }, [selectedVariant])

  const hasVariantWithBanner = product.variants?.[0]?.show_category_products_banner === 1

  useEffect(() => {
    if (hasVariantWithBanner && product.categories && product.categories.length > 0) {
      const fetchRelatedProducts = async () => {
        try {
          const categories = product.categories
          if (!categories || categories.length === 0) {
            return
          }

          const categoryId = categories[0]?.id
          if (!categoryId) {
            return
          }

          const url = `/api/store-proxy/products?category_id=${categoryId}&limit=5`

          const response = await fetch(url)

          if (response.ok) {
            const data = await response.json()

            const filteredProducts = data.data
              .filter((p: StoreProduct) => p.id !== product.id)
              .slice(0, 5)
            setRelatedProducts(filteredProducts)
          } else {
          }
        } catch (error) {
          console.error('Error fetching related products:', error)
        }
      }
      fetchRelatedProducts()
    }
  }, [hasVariantWithBanner, product.categories, product.id, product.variants])

  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const { toast } = useToast()

  const handleFavoriteToggle = async () => {
    if (!isClient) return

    const customer = await getCustomer()
    if (!customer) {
      router.push("/account/sign-in")
      return
    }

    const productVariant = selectedVariant
    if (!productVariant) return

    startTransition(async () => {
      try {
        if (isFavorited) {
          const favoriteId = productVariant.id
          if (!favoriteId) {
             console.error("Favorite ID not found for variant:", productVariant.id)
             return
          }
          await removeFavorite(String(favoriteId))
          setIsFavorited(false)
          toast({ title: t("product.removeFromFavoritesSuccess") })
        } else {
          await addFavorite(String(productVariant.id))
          setIsFavorited(true)
          toast({ title: t("product.addToFavoritesSuccess") })
        }
      } catch (error: any) {
        console.error("An unexpected error occurred during favorite toggle:", error)
        toast({
          title: t("product.favoriteError"),
          description: error.message || t("product.favoriteError"),
          variant: "destructive",
        })
      }
    })
  }

  let thumbnailUrl = null

  if (selectedVariant?.images && selectedVariant.images.length > 0) {
    const thumbnail =
      selectedVariant.images?.find((image) => image.is_thumbnail === 1) ||
      selectedVariant.images?.[0]

    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  } else {
    const thumbnail = product.images?.find((image) => image.is_thumbnail === 1)
    if (thumbnail) {
      thumbnailUrl = resolveImageUrl(thumbnail.image_key)
    }
  }

  return (
    <div className="content-container px-4 small:px-6">
      <div
        className="relative mt-6 flex flex-col gap-6 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm small:p-6 small:flex-row small:items-start"
        data-testid="product-container"
      >
        <Container className="relative aspect-[34/34] h-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 lg:w-[32%]">
          {thumbnailUrl ? (
            <Image
              src={thumbnailUrl}
              className="absolute inset-0 rounded-xl"
              alt={t("product.productImage")}
              fill
              style={{
                objectFit: "fill",
              }}
            />
          ) : (
            <PlaceholderImage className="absolute inset-0 rounded-xl w-full h-full" />
          )}
        </Container>
        <div className="flex h-full w-full flex-col gap-3 lg:w-3/5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h2 className="text-3xl font-extrabold tracking-tight text-slate-900">
                  {normalizeTextEncoding(product.title ?? "")}
                </h2>
                {!product.discountable && (
                  <div className="flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1">
                    <span className="text-xs font-semibold text-red-700">
                      {t("product.promotionNotApplicable")}
                    </span>
                  </div>
                )}
              </div>
              {product.product_info && (
                <div className="text-base text-slate-600">
                  {product.product_info}
                </div>
              )}
            </div>
            <div className="flex flex-row flex-wrap gap-3 md:h-10 md:flex-nowrap md:overflow-x-auto md:no-scrollbar">
              <div className="flex flex-row gap-2 items-center">
                <Rating
                  name="simple-controlled"
                  value={averageRating}
                  precision={0.5}
                  size="small"
                  readOnly
                />
                <span className="text-sm text-slate-600">({averageRating})</span>
              </div>
              <Separator orientation="vertical" className="bg-slate-200" />
              <div className="flex cursor-pointer flex-row items-center gap-2 text-slate-600 hover:text-orange-600">
                <MessagesSquare className="h-5 w-5 text-orange-500" />
                <span className="text-sm min-w-[6rem]">{reviewCount} {t("product.comments")}</span>
              </div>
              <Separator orientation="vertical" className="bg-slate-200" />
              <div className="flex gap-4">
                <Button
                  className="rounded-full border border-orange-200 bg-orange-50 px-3 text-orange-700 hover:bg-orange-100"
                  size={"sm"}
                >
                  <div className="flex flex-row gap-1 items-center">
                    {" "}
                    <MessageCirclePlus className="w-3 h-3" /> {t("product.rateProduct")}
                  </div>
                </Button>
                <Separator orientation="vertical" className="bg-slate-200" />
                <Button
                  className="rounded-full border border-orange-200 bg-orange-50 px-3 text-orange-700 hover:bg-orange-100"
                  size={"sm"}
                  onClick={handleFavoriteToggle}
                  disabled={isPending || !isClient}
                >
                  <div className="flex flex-row gap-1 items-center">
                    {" "}
                    <Heart className="w-3 h-3" fill={isFavorited ? "orange" : "none"} /> {isFavorited ? t("product.removeFromFavorites") : t("product.addToFavorites")}
                  </div>
                </Button>
              </div>
            </div>
          </div>

          <Separator className="bg-slate-200" />
          <ProductSelect
            product={product}
            selectedVariant={selectedVariant}
            setSelectedVariant={setSelectedVariant}
            cart={cart}
          />
        </div>
      </div>
    </div>
  )
}

export default ProductField
